import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import { PaginationSchema } from 'types/pagination';
import { z } from 'zod/v4';

import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from '../../../shared/hooks/utils';

import { AiCallSchema } from './use-load-ai-call';

const AiCallsSchema = z.object({
  data: z.array(AiCallSchema),
  pagination: PaginationSchema,
});

export type AiCalls = z.infer<typeof AiCallsSchema>;

export type LoadAiCallsVariables = {
  page: number;
  pageLimit: number;
  sortBy?: 'invoiceReference' | 'debtorName' | 'duration' | 'outcome' | 'endReason' | 'startedAt';
  sortOrder?: 'asc' | 'desc';
  invoiceReference?: string;
  debtorName?: string;
  startDate?: Date;
  endDate?: Date;
  durationMin?: number;
  durationMax?: number;
  outcome?: string;
  endReason?: string;
};

export async function loadAiCallsFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: LoadAiCallsVariables,
) {
  const instance = await axiosInstance;

  const { data } = await instance.get('/ai_calls', { params: variables });

  const parseResult = AiCallsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'aiCall');
    throw new SilentError();
  }

  return parseResult.data;
}

const emptyAiCalls: AiCalls = {
  data: [],
  pagination: {
    totalObjects: 0,
    totalPages: 0,
    pageLimit: 0,
  },
};

export function useLoadAiCalls(
  variables: LoadAiCallsVariables,
  options: QueryOptions<AiCalls> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<AiCalls, ApiError>({
    queryKey: ['aiCalls', variables],
    queryFn: context => loadAiCallsFn(axiosInstance, context.queryKey[1] as LoadAiCallsVariables),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;

      return emptyAiCalls;
    },
    ...options,
  });

  return addResourceNameToQueryResult<AiCalls, ApiError, 'aiCalls', 'PreventUndefined'>(
    'aiCalls',
    queryResult,
    emptyAiCalls,
  );
}
